/**
 * Session Storage Utilities for Tab-Isolated Authentication
 * 
 * This module provides utilities for storing authentication data in sessionStorage
 * to achieve tab isolation. Each browser tab maintains its own independent session.
 * 
 * Key Benefits:
 * - Tab Isolation: Multiple users can be logged in different tabs simultaneously
 * - Auto Cleanup: Data clears when tab closes
 * - Persists on Refresh: User stays logged in during same tab session
 * - Security: No cross-tab data leakage
 */

import { USER_DATA } from '@/constants/constants';
import { Permissions, ApiPermissionsResponse } from '@/types/permissions';

// ============================================
// Types
// ============================================

export type UserType = "superadmin" | "admin" | "enduser" | "manager" | "unknown";

export interface AuthData {
  token?: string;
  user?: {
    email: string;
    name: string;
    id?: string;
    language?: string;
    is_superuser?: boolean;
    feature_name?: string;
    feature_id?: number;
    timezone_id?: string;
  };
  role?: string;
  permissions?: Permissions[];
  permissionObjects?: ApiPermissionsResponse;
  issuperUser?: boolean;
  [key: string]: any;
}

interface UserTypeData {
  userType: UserType;
  featureId: number;
  userId?: number;
  timezoneId?: string;
  displayName?: string;
  timestamp: number;
  version: string;
}

// ============================================
// Core Session Storage Operations
// ============================================

const sessionStorageOps = {
  /**
   * Set item in sessionStorage
   */
  setItem: (key: string, value: string): void => {
    if (typeof window !== "undefined" && window.sessionStorage) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        console.error(`Failed to set item in sessionStorage (${key}):`, error);
      }
    }
  },

  /**
   * Get item from sessionStorage
   */
  getItem: (key: string): string | null => {
    if (typeof window !== "undefined" && window.sessionStorage) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        console.error(`Failed to get item from sessionStorage (${key}):`, error);
        return null;
      }
    }
    return null;
  },

  /**
   * Remove item from sessionStorage
   */
  removeItem: (key: string): void => {
    if (typeof window !== "undefined" && window.sessionStorage) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.error(`Failed to remove item from sessionStorage (${key}):`, error);
      }
    }
  },

  /**
   * Check if sessionStorage is available
   */
  isAvailable: (): boolean => {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) {
        return false;
      }

      const testKey = "__sessionStorage_test__";
      const testValue = "test";
      sessionStorage.setItem(testKey, testValue);
      const retrieved = sessionStorage.getItem(testKey);
      sessionStorage.removeItem(testKey);
      return retrieved === testValue;
    } catch (error) {
      return false;
    }
  },

  /**
   * Clear all items from sessionStorage
   */
  clear: (): void => {
    if (typeof window !== "undefined" && window.sessionStorage) {
      try {
        sessionStorage.clear();
      } catch (error) {
        console.error("Failed to clear sessionStorage:", error);
      }
    }
  },
};

// ============================================
// Authentication Data Management
// ============================================

/**
 * Save authentication data to sessionStorage
 * @param authData - Complete authentication data including token, user info, permissions
 * @returns boolean indicating success
 */
export const saveAuthToSession = (authData: AuthData): boolean => {
  try {
    if (!sessionStorageOps.isAvailable()) {
      console.warn("sessionStorage not available, skipping auth save");
      return false;
    }

    // Store complete user data including permissions and token
    const dataToStore = {
      ...authData,
      permissions: authData.permissions || [],
      permissionObjects: authData.permissionObjects || {},
      timestamp: Date.now(),
    };

    const serialized = JSON.stringify(dataToStore);
    sessionStorageOps.setItem(USER_DATA, serialized);
    
    return true;
  } catch (error) {
    console.error("Failed to save auth data to sessionStorage:", error);
    return false;
  }
};

/**
 * Load authentication data from sessionStorage
 * @returns AuthData or null if not found
 */
export const loadAuthFromSession = (): AuthData | null => {
  try {
    if (!sessionStorageOps.isAvailable()) {
      return null;
    }

    const stored = sessionStorageOps.getItem(USER_DATA);
    if (!stored) {
      return null;
    }

    const parsed: AuthData = JSON.parse(stored);
    return parsed;
  } catch (error) {
    console.error("Failed to load auth data from sessionStorage:", error);
    // Clear corrupted data
    clearAuthFromSession();
    return null;
  }
};

/**
 * Clear authentication data from sessionStorage
 */
export const clearAuthFromSession = (): void => {
  try {
    sessionStorageOps.removeItem(USER_DATA);
  } catch (error) {
    console.error("Failed to clear auth data from sessionStorage:", error);
  }
};

/**
 * Get user permissions from sessionStorage
 * @returns Array of permissions
 */
export const getUserPermissionsFromSession = (): Permissions[] => {
  const authData = loadAuthFromSession();
  return authData?.permissions || [];
};

/**
 * Get user permission objects from sessionStorage
 * @returns Permission objects
 */
export const getUserPermissionObjectsFromSession = (): ApiPermissionsResponse => {
  const authData = loadAuthFromSession();
  return authData?.permissionObjects || {};
};

/**
 * Update permissions in existing session auth data
 * @param permissions - New permissions array
 */
export const updateAuthPermissionsInSession = (permissions: Permissions[]): void => {
  const currentAuthData = loadAuthFromSession();
  if (currentAuthData) {
    const updatedAuthData = {
      ...currentAuthData,
      permissions: permissions,
    };
    saveAuthToSession(updatedAuthData);
  }
};

// ============================================
// UserType Management
// ============================================

const USER_TYPE_SESSION_KEY = "finloc_user_type_session";

/**
 * Save user type to sessionStorage
 * @param userType - User type
 * @param featureId - Feature ID
 * @param userId - User ID (optional)
 * @returns boolean indicating success
 */
export const saveUserTypeToSession = (userType: UserType, featureId: number, userId?: number, timezoneId?: string, displayName?: string): boolean => {
  try {
    if (!sessionStorageOps.isAvailable()) {
      console.warn("sessionStorage not available, skipping userType save");
      return false;
    }

    const dataWithMetadata: UserTypeData = {
      userType,
      featureId,
      userId,
      timezoneId,
      displayName,
      timestamp: Date.now(),
      version: "1.0",
    };

    const serialized = JSON.stringify(dataWithMetadata);
    sessionStorageOps.setItem(USER_TYPE_SESSION_KEY, serialized);
    return true;
  } catch (error) {
    console.error("Failed to save userType to sessionStorage:", error);
    return false;
  }
};

/**
 * Load user type from sessionStorage
 * @returns UserType data or null
 */
export const loadUserTypeFromSession = (): { userType: UserType; featureId: number; userId?: number; timezoneId?: string; displayName?: string } | null => {
  try {
    if (!sessionStorageOps.isAvailable()) {
      return null;
    }

    const stored = sessionStorageOps.getItem(USER_TYPE_SESSION_KEY);
    if (!stored) {
      return null;
    }

    const parsed: UserTypeData = JSON.parse(stored);
    return {
      userType: parsed.userType,
      featureId: parsed.featureId,
      userId: parsed.userId,
      timezoneId: parsed.timezoneId,
      displayName: parsed.displayName,
    };
  } catch (error) {
    console.error("Failed to load userType from sessionStorage:", error);
    clearUserTypeFromSession();
    return null;
  }
};

/**
 * Clear user type from sessionStorage
 */
export const clearUserTypeFromSession = (): void => {
  try {
    sessionStorageOps.removeItem(USER_TYPE_SESSION_KEY);
  } catch (error) {
    console.error("Failed to clear userType from sessionStorage:", error);
  }
};

// ============================================
// Migration Utilities
// ============================================

// Migration flag keys (stored in sessionStorage to track per-tab)
const MIGRATION_AUTH_FLAG = "finloc_auth_migrated";
const MIGRATION_USERTYPE_FLAG = "finloc_usertype_migrated";

/**
 * Migrate authentication data from localStorage to sessionStorage
 * This should be called once during app initialization to migrate existing users
 * Uses a session flag to ensure migration runs ONLY ONCE per tab
 * @returns boolean indicating if migration occurred
 */
export const migrateAuthFromLocalStorage = (): boolean => {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    // Check if migration already ran in this tab session
    const migrationFlag = sessionStorage.getItem(MIGRATION_AUTH_FLAG);
    if (migrationFlag === "true") {
      return false; // Already migrated in this tab, skip
    }

    // Mark migration as attempted for this tab (even if no data to migrate)
    sessionStorage.setItem(MIGRATION_AUTH_FLAG, "true");

    // Check if data already exists in sessionStorage (user already logged in this tab)
    const sessionStorageData = sessionStorage.getItem(USER_DATA);
    if (sessionStorageData) {
      return false; // This tab already has its own session data, don't overwrite
    }

    // Only migrate if localStorage has data AND sessionStorage is empty
    const localStorageData = localStorage.getItem(USER_DATA);
    if (!localStorageData) {
      return false; // Nothing to migrate
    }

    // Migrate: Copy to sessionStorage ONLY for this tab
    sessionStorage.setItem(USER_DATA, localStorageData);
    
    // DO NOT clear localStorage here - other tabs might need it for their migration
    // localStorage will naturally become obsolete as users re-login

    console.log("Successfully migrated auth data from localStorage to sessionStorage for this tab");
    return true;
  } catch (error) {
    console.error("Failed to migrate auth data:", error);
    return false;
  }
};

/**
 * Migrate userType from localStorage to sessionStorage
 * Uses a session flag to ensure migration runs ONLY ONCE per tab
 * @returns boolean indicating if migration occurred
 */
export const migrateUserTypeFromLocalStorage = (): boolean => {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    // Check if migration already ran in this tab session
    const migrationFlag = sessionStorage.getItem(MIGRATION_USERTYPE_FLAG);
    if (migrationFlag === "true") {
      return false; // Already migrated in this tab, skip
    }

    // Mark migration as attempted for this tab
    sessionStorage.setItem(MIGRATION_USERTYPE_FLAG, "true");

    // Check if data already exists in sessionStorage
    const sessionStorageData = sessionStorage.getItem(USER_TYPE_SESSION_KEY);
    if (sessionStorageData) {
      return false; // This tab already has its own data, don't overwrite
    }

    // Check if data exists in localStorage (old key)
    const localUserTypeKey = "finloc_user_type";
    const localStorageData = localStorage.getItem(localUserTypeKey);
    if (!localStorageData) {
      return false; // Nothing to migrate
    }

    // Parse localStorage data to ensure it's valid
    try {
      const parsed = JSON.parse(localStorageData);
      // Migrate to sessionStorage with current timestamp
      const migratedData = {
        userType: parsed.userType,
        featureId: parsed.featureId,
        timestamp: Date.now(),
        version: "1.0",
      };
      sessionStorage.setItem(USER_TYPE_SESSION_KEY, JSON.stringify(migratedData));
      
      // DO NOT clear localStorage here - other tabs might need it
      
      console.log("Successfully migrated userType from localStorage to sessionStorage for this tab");
      return true;
    } catch (parseError) {
      console.error("Failed to parse userType data during migration:", parseError);
      return false;
    }
  } catch (error) {
    console.error("Failed to migrate userType:", error);
    return false;
  }
};

// ============================================
// Utility Functions
// ============================================

/**
 * Check if sessionStorage is available and functioning
 * @returns boolean
 */
export const isSessionStorageAvailable = (): boolean => {
  return sessionStorageOps.isAvailable();
};

/**
 * Clear all session data (auth + userType)
 */
export const clearAllSessionData = (): void => {
  clearAuthFromSession();
  clearUserTypeFromSession();
};

/**
 * Get session storage info for debugging
 */
export const getSessionStorageInfo = () => {
  try {
    if (!sessionStorageOps.isAvailable()) {
      return { available: false };
    }

    const authData = sessionStorageOps.getItem(USER_DATA);
    const userTypeData = sessionStorageOps.getItem(USER_TYPE_SESSION_KEY);

    return {
      available: true,
      hasAuth: !!authData,
      hasUserType: !!userTypeData,
      authSize: authData ? new Blob([authData]).size : 0,
      userTypeSize: userTypeData ? new Blob([userTypeData]).size : 0,
    };
  } catch (error: any) {
    return { available: false, error: error.message };
  }
};

/**
 * Manual cleanup utility to remove old localStorage data
 * Call this ONLY after all users have migrated to sessionStorage
 * WARNING: This will affect all tabs, so only use when migrations are complete
 */
export const cleanupLegacyLocalStorage = (): void => {
  try {
    if (typeof window === "undefined") {
      return;
    }
    
    // Remove old auth data from localStorage
    localStorage.removeItem(USER_DATA);
    
    // Remove old userType data from localStorage
    localStorage.removeItem("finloc_user_type");
    
    console.log("Cleaned up legacy localStorage data");
  } catch (error) {
    console.error("Failed to cleanup legacy localStorage:", error);
  }
};

// ============================================
// Cross-Tab Navigation Utilities
// ============================================

/**
 * Open a URL in a new tab with session transfer
 * 
 * Since sessionStorage doesn't persist across tabs by default, this function
 * temporarily stores all sessionStorage data in localStorage under '_transferSession'.
 * The receiving tab (via ClientLayout) automatically detects and restores this data.
 * 
 * @param url - The URL to open in a new tab
 * 
 * @example
 * ```typescript
 * openInNewTabWithSession('/dashboard/details?id=123');
 * ```
 */
export const openInNewTabWithSession = (url: string): void => {
  try {
    if (typeof window === "undefined") {
      console.warn("Cannot open new tab in non-browser environment");
      return;
    }

    // Transfer sessionStorage to localStorage temporarily
    const sessionData: { [key: string]: string } = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        sessionData[key] = sessionStorage.getItem(key) || '';
      }
    }
    
    // Store in localStorage with a flag
    localStorage.setItem('_transferSession', JSON.stringify(sessionData));
    
    // Open new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error("Failed to open new tab with session transfer:", error);
    // Fallback: just open the tab without session transfer
    if (typeof window !== "undefined") {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }
};

// ============================================
// Exports
// ============================================

export {
  sessionStorageOps,
  USER_TYPE_SESSION_KEY,
};

export type { UserTypeData };
